import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../../src/functions/useLanguage';
import { wt } from '../../i18n';
import { websitePath } from '../../lib/routes';

const META = {
  title: 'The Habit Loop: Building Daily Pet Care Routines That Stick | KodaCare Blog',
  description:
    'Simple routines for meds, meals, hydration, and activity. Learn how KodaCare keeps daily check-ins consistent without burnout.',
  slug: 'the-habit-loop',
  dateISO: '2026-03-28',
  readMins: 5,
  tag: 'Routines',
};

type Lang = 'English' | 'French' | 'German' | 'Portuguese' | 'Spanish';

const LOCALE_BY_LANGUAGE: Record<Lang, string> = {
  English: 'en-GB',
  French: 'fr-FR',
  German: 'de-DE',
  Portuguese: 'pt-PT',
  Spanish: 'es-ES',
};

export function TheHabitLoop() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);
  const activeLanguage = language as Lang;

  const titleByLanguage: Record<string, string> = {
    English: 'The Habit Loop: Building Daily Pet Care Routines That Stick',
    French: 'La boucle d’habitudes : des routines de soin qui tiennent',
    German: 'Die Gewohnheitsschleife: Routinen für die tägliche Tierpflege',
    Portuguese: 'O ciclo do hábito: rotinas diárias que funcionam',
    Spanish: 'El ciclo del hábito: rutinas diarias que sí se mantienen',
  };

  const descriptionByLanguage: Record<Lang, string> = {
    English:
      'Simple routines for meds, meals, hydration, and activity. Learn how KodaCare keeps daily check-ins consistent without burnout.',
    French:
      'Des routines simples pour les médicaments, repas, hydratation et activité. KodaCare aide à rester constant sans s’épuiser.',
    German:
      'Einfache Routinen für Medikamente, Mahlzeiten, Flüssigkeit und Aktivität. KodaCare hält tägliche Check-ins konstant.',
    Portuguese:
      'Rotinas simples para medicação, refeições, hidratação e atividade. O KodaCare ajuda a manter constância sem desgaste.',
    Spanish:
      'Rutinas simples para medicación, comidas, hidratación y actividad. KodaCare mantiene la constancia sin desgaste.',
  };

  const localizedTitle = `${titleByLanguage[activeLanguage] ?? titleByLanguage.English} | KodaCare Blog`;
  const localizedDescription = descriptionByLanguage[activeLanguage] ?? descriptionByLanguage.English;

  const tagLabel =
    META.tag === 'Routines'
      ? t('blog.tag.routines')
      : META.tag;

  const isEnglishContent = activeLanguage === 'English';
  const translatedSummary: Record<Exclude<Lang, 'English'>, {
    intro: string;
    sections: { heading: string; paragraphs: string[] }[];
    closing: string;
    series: string;
  }> = {
    French: {
      intro:
        'Les routines de soin fonctionnent quand elles sont simples et prévisibles. L’objectif est de créer une boucle quotidienne facile à tenir.',
      sections: [
        {
          heading: '1. Ancrer les soins à un moment fixe',
          paragraphs: [
            'Associez médicaments et repas, ou promenades et hydratation.',
            'Un repère stable facilite la régularité.'
          ],
        },
        {
          heading: '2. Check-ins rapides',
          paragraphs: [
            'Quelques notes sur l’appétit, l’énergie et la respiration suffisent.',
            'KodaCare garde ces données sans effort.'
          ],
        },
        {
          heading: '3. Ajuster sans culpabilité',
          paragraphs: [
            'Les journées changent: adaptez la routine et reprenez le fil.',
            'La constance se construit dans la durée.'
          ],
        },
      ],
      closing: 'Une boucle d’habitudes simple protège votre animal et réduit le stress quotidien.',
      series: 'KodaCare Journal · Article 4 sur 4',
    },
    German: {
      intro:
        'Pflege-Routinen bleiben am besten, wenn sie einfach und vorhersehbar sind. Eine kleine Gewohnheitsschleife reicht oft aus.',
      sections: [
        {
          heading: '1. An feste Anker koppeln',
          paragraphs: [
            'Medikamente mit Mahlzeiten verbinden, Spaziergänge mit Trinkpausen.',
            'Ein stabiler Anker erleichtert die Kontinuität.'
          ],
        },
        {
          heading: '2. Kurze tägliche Check-ins',
          paragraphs: [
            'Notieren Sie Appetit, Energie und Atmung in wenigen Sekunden.',
            'KodaCare hält die Routine leicht.'
          ],
        },
        {
          heading: '3. Flexibel bleiben',
          paragraphs: [
            'Nicht jeder Tag ist gleich. Passen Sie an und starten Sie neu.',
            'Kontinuität entsteht über Zeit, nicht Perfektion.'
          ],
        },
      ],
      closing: 'Eine einfache Gewohnheitsschleife schützt Ihr Tier und reduziert Stress.',
      series: 'KodaCare Journal · Artikel 4 von 4',
    },
    Portuguese: {
      intro:
        'Rotinas funcionam quando são simples e previsíveis. Uma pequena sequência diária é suficiente.',
      sections: [
        {
          heading: '1. Use âncoras fixas',
          paragraphs: [
            'Junte medicação às refeições e atividade à hidratação.',
            'Um horário fixo ajuda a manter constância.'
          ],
        },
        {
          heading: '2. Check-ins rápidos',
          paragraphs: [
            'Registe apetite, energia e respiração em poucos segundos.',
            'O KodaCare simplifica o registo.'
          ],
        },
        {
          heading: '3. Ajuste sem culpa',
          paragraphs: [
            'Dias mudam. Adapte a rotina e retome.',
            'Consistência é construída ao longo do tempo.'
          ],
        },
      ],
      closing: 'Um ciclo simples protege o seu animal e reduz stress diário.',
      series: 'KodaCare Journal · Artigo 4 de 4',
    },
    Spanish: {
      intro:
        'Las rutinas se sostienen cuando son simples y previsibles. Una pequeña secuencia diaria es suficiente.',
      sections: [
        {
          heading: '1. Anclas fijas',
          paragraphs: [
            'Vincula medicación con comidas y actividad con hidratación.',
            'Un horario estable facilita la constancia.'
          ],
        },
        {
          heading: '2. Check-ins cortos',
          paragraphs: [
            'Registra apetito, energía y respiración en minutos.',
            'KodaCare mantiene el registro sencillo.'
          ],
        },
        {
          heading: '3. Ajusta sin culpa',
          paragraphs: [
            'Los días cambian. Ajusta la rutina y vuelve a empezar.',
            'La constancia se construye con el tiempo.'
          ],
        },
      ],
      closing: 'Un ciclo simple protege a tu mascota y reduce el estrés diario.',
      series: 'KodaCare Journal · Artículo 4 de 4',
    },
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = localizedTitle;

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.content;
    metaDesc.content = localizedDescription;

    const upsertMeta = (selector: string, attr: 'property' | 'name', value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, selector.match(/\[.+?="(.+?)"\]/)?.[1] ?? '');
        document.head.appendChild(el);
      }
      const previous = el.content;
      el.content = value;
      return { el, created, previous };
    };

    const updates = [
      upsertMeta('meta[property="og:title"]', 'property', localizedTitle),
      upsertMeta('meta[property="og:description"]', 'property', localizedDescription),
      upsertMeta('meta[property="og:type"]', 'property', 'article'),
      upsertMeta('meta[property="og:url"]', 'property', `https://kodacare.pet/blog/${META.slug}`),
      upsertMeta('meta[name="twitter:card"]', 'name', 'summary_large_image'),
      upsertMeta('meta[name="twitter:title"]', 'name', localizedTitle),
      upsertMeta('meta[name="twitter:description"]', 'name', localizedDescription),
      upsertMeta('meta[name="twitter:image"]', 'name', 'https://kodacare.pet/icons/og-image.png'),
      upsertMeta('meta[property="og:image"]', 'property', 'https://kodacare.pet/icons/og-image.png'),
    ];

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalCreated = !canonical;
    const previousCanonical = canonical?.href ?? '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://kodacare.pet/blog/${META.slug}`;

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.content = prevDesc;

      updates.forEach(({ el, created, previous }) => {
        if (created) {
          el.remove();
        } else {
          el.content = previous;
        }
      });

      if (canonical) {
        if (canonicalCreated) {
          canonical.remove();
        } else {
          canonical.href = previousCanonical;
        }
      }
    };
  }, [localizedDescription, localizedTitle]);

  return (
    <main className="min-h-screen bg-white">
      <div className="bg-[#0d2744] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="flex items-center gap-2 text-primary-brown/80 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> {t('blog.backToBlog')}
          </button>
          <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-primary-brown/15 text-primary-brown mb-4">
            {tagLabel}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            {titleByLanguage[language] ?? titleByLanguage.English}
          </h1>
          <div className="flex items-center gap-3 text-sm text-primary-brown/80">
            <span>{new Date(`${META.dateISO}T00:00:00`).toLocaleDateString(LOCALE_BY_LANGUAGE[activeLanguage] ?? 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span>·</span>
            <span>{META.readMins} {t('blog.minRead')}</span>
            <span>·</span>
            <span>{t('blog.authorTeam')}</span>
          </div>
        </div>
      </div>

      {!isEnglishContent && (
        <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
          <p className="text-xl text-gray-600 leading-relaxed mb-8 not-prose">
            {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].intro}
          </p>
          {translatedSummary[activeLanguage as Exclude<Lang, 'English'>].sections.map((section) => (
            <React.Fragment key={section.heading}>
              <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">{section.heading}</h2>
              {section.paragraphs.map((paragraph, idx) => (
                <p key={`${section.heading}-${idx}`}>{paragraph}</p>
              ))}
            </React.Fragment>
          ))}
          <p>{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].closing}</p>
          <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
            <p className="text-sm text-gray-600 italic leading-relaxed">
              <strong className="text-[#0d2744]">{translatedSummary[activeLanguage as Exclude<Lang, 'English'>].series}</strong>
            </p>
          </div>
        </article>
      )}

      <article hidden={!isEnglishContent} className="max-w-3xl mx-auto px-6 lg:px-10 py-16 prose prose-slate prose-lg max-w-none">
        <p>
          The most effective care plans are the ones you can actually keep. For pets, that usually
          means simple routines: meds at the same time, meals on a consistent schedule, and quick
          daily check-ins.
        </p>

        <p>
          KodaCare is built to support that habit loop, so you don’t need to rely on memory or
          long to‑do lists.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          1. Anchor Routines to Existing Cues
        </h2>

        <p>
          Link care tasks to a moment that already happens every day—breakfast, a morning walk, or
          bedtime. When meds and meals share the same cue, consistency increases.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          2. Keep Check-Ins Short
        </h2>

        <p>
          A 60‑second check-in beats a 10‑minute log you’ll eventually skip. Record appetite,
          energy, breathing, and any meds given. KodaCare keeps the process light and quick.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          3. Make It Visible to Everyone
        </h2>

        <p>
          If multiple people care for your pet, a shared log prevents missed doses and mixed
          messages. Everyone sees the same timeline and knows what happened today.
        </p>

        <h2 className="text-2xl font-black text-[#0d2744] mt-12 mb-4">
          4. Review Weekly, Adjust Gently
        </h2>

        <p>
          Look back once a week. If a routine feels too heavy, simplify it. Consistency isn’t about
          perfection—it’s about staying in rhythm.
        </p>

        <p>
          <strong>Small routines, repeated daily, are what protect long‑term health.</strong>
        </p>

        <div className="not-prose mt-12 p-6 bg-gray-50 border border-gray-200 rounded-2xl">
          <p className="text-sm text-gray-600 italic leading-relaxed">
            <strong className="text-[#0d2744]">KodaCare Journal · Article 4 of 4</strong>
            <br />
            This concludes the KodaCare Journal series. If you’d like more pet care playbooks and
            tracking tips, keep an eye on the blog.
          </p>
        </div>
      </article>

      <div className="border-t border-gray-100 py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-gray-500">{t('blog.moreFromTeam')}</p>
            <p className="font-bold text-[#0d2744] text-lg">{t('blog.readAllArticles')}</p>
          </div>
          <button
            onClick={() => navigate(websitePath('/blog'))}
            className="px-8 py-3 bg-[#0d2744] text-white rounded-xl font-bold hover:bg-[#1a3a5c] transition-colors"
          >
            ← {t('blog.backToBlog')}
          </button>
        </div>
      </div>
    </main>
  );
}
